import { useCallback, useMemo, type CSSProperties } from "react";

import {
  Form,
  FormField,
  FormikErrorMessages,
  FormikForm,
  GridTwoColumns,
  SkiliticsIcon,
} from "@skilitics/designer-ui-components";
import { useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { useEditingCss } from "./EditingCssPropertiesContext";
import { FieldCssInput } from "./FieldCssInput";

export type SizeFormValues = Pick<
  CSSProperties,
  "width" | "height" | "minWidth" | "minHeight" | "maxWidth" | "maxHeight"
>;

type Props = {
  isWidthInactive: boolean;
  isHeightInactive: boolean;
  useMinimums: boolean;
  useMaximums: boolean;
};

export const SizeForm = ({ isWidthInactive, isHeightInactive, useMinimums, useMaximums }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);
  const { cssProperties, onChange } = useEditingCss();

  const initialValues: SizeFormValues = useMemo(
    () => ({
      width: cssProperties.width,
      height: cssProperties.height,
      minWidth: cssProperties.minWidth,
      minHeight: cssProperties.minHeight,
      maxWidth: cssProperties.maxWidth,
      maxHeight: cssProperties.maxHeight,
    }),
    [cssProperties],
  );

  const handleSubmit = useCallback(
    (values: SizeFormValues) => {
      // Determine if width/height changed while in non-fixed mode
      const widthChanged = values.width !== cssProperties.width && isWidthInactive;
      const heightChanged = values.height !== cssProperties.height && isHeightInactive;

      onChange({
        cssProperties: values,
        layoutSettings:
          widthChanged || heightChanged
            ? {
                ...(widthChanged ? { horizontalBehavior: "fixed" } : {}),
                ...(heightChanged ? { verticalBehavior: "fixed" } : {}),
              }
            : undefined,
      });
    },
    [cssProperties.height, cssProperties.width, isHeightInactive, isWidthInactive, onChange],
  );

  return (
    <FormikForm<SizeFormValues>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      submitOnBlur
      dataTest="size-form"
      validateOnChange={false}
    >
      <Form>
        <GridTwoColumns>
          <FormField<SizeFormValues> name="width">
            <FieldCssInput label="W" tooltip={formatMessage("width")} inactive={isWidthInactive} />
          </FormField>

          <FormField<SizeFormValues> name="height">
            <FieldCssInput label="H" tooltip={formatMessage("height")} inactive={isHeightInactive} />
          </FormField>

          {useMinimums && (
            <>
              <FormField<SizeFormValues> name="minWidth">
                <FieldCssInput
                  label={<SkiliticsIcon icon="minimum-width" />}
                  tooltip={formatMessage("minWidth")}
                  placeholder={formatMessage("minWidth")}
                />
              </FormField>

              <FormField<SizeFormValues> name="minHeight">
                <FieldCssInput
                  label={<SkiliticsIcon icon="minimum-width" rotation={90} />}
                  tooltip={formatMessage("minHeight")}
                  placeholder={formatMessage("minHeight")}
                />
              </FormField>
            </>
          )}

          {useMaximums && (
            <>
              <FormField<SizeFormValues> name="maxWidth">
                <FieldCssInput
                  label={<SkiliticsIcon icon="maximum-width" />}
                  tooltip={formatMessage("maxWidth")}
                  placeholder={formatMessage("maxWidth")}
                />
              </FormField>

              <FormField<SizeFormValues> name="maxHeight">
                <FieldCssInput
                  label={<SkiliticsIcon icon="maximum-width" rotation={90} />}
                  tooltip={formatMessage("maxHeight")}
                  placeholder={formatMessage("maxHeight")}
                />
              </FormField>
            </>
          )}
        </GridTwoColumns>

        <FormikErrorMessages />
      </Form>
    </FormikForm>
  );
};

const formatStrings = {
  width: {
    en: "Width",
  },
  height: {
    en: "Height",
  },
  maxWidth: {
    en: "Max Width",
  },
  maxHeight: {
    en: "Max Height",
  },
  minWidth: {
    en: "Min Width",
  },
  minHeight: {
    en: "Min Height",
  },
} satisfies FormatMessages;
